/**
 * Throw, apex, and catch detection from ball tracks.
 * Attributes events to left/right hands.
 */
import type { BallTrack } from './BallTracker';
import type { HandPosition } from './HandTracker';

export interface ThrowEvent {
  ballId: number;
  hand: 'left' | 'right';
  apexY: number;
  timestampMs: number;
}

export interface CatchEvent {
  ballId: number;
  hand: 'left' | 'right';
  timestampMs: number;
}

export interface EventDetectorState {
  totalThrows: number;
  totalCatches: number;
  leftThrows: number;
  rightThrows: number;
  leftCatches: number;
  rightCatches: number;
  peakHeights: number[];
  lastEvents: Array<ThrowEvent | CatchEvent>;
}

const VY_UP_THRESHOLD = 3;
const VY_DOWN_THRESHOLD = -3;
const CATCH_SPEED_THRESHOLD = 15;
const HAND_ATTRIBUTION_RADIUS = 80;
const EVENT_COOLDOWN_MS = 200;

interface BallHistory {
  ballId: number;
  prevVy: number;
  wasGoingUp: boolean;
  lastThrowMs: number;
  lastCatchMs: number;
}

export class EventDetector {
  private ballHistory = new Map<number, BallHistory>();
  private totalThrows = 0;
  private totalCatches = 0;
  private leftThrows = 0;
  private rightThrows = 0;
  private leftCatches = 0;
  private rightCatches = 0;
  private peakHeights: number[] = [];
  private lastEvents: Array<ThrowEvent | CatchEvent> = [];
  private frameHeight = 480;

  setFrameHeight(h: number): void {
    this.frameHeight = h;
  }

  update(
    tracks: BallTrack[],
    hands: HandPosition[],
    timestampMs: number
  ): { throwCount: number; catchCount: number; throws: ThrowEvent[]; catches: CatchEvent[] } {
    const newThrows: ThrowEvent[] = [];
    const newCatches: CatchEvent[] = [];

    for (const track of tracks) {
      let hist = this.ballHistory.get(track.id);
      if (!hist) {
        hist = {
          ballId: track.id,
          prevVy: 0,
          wasGoingUp: false,
          lastThrowMs: 0,
          lastCatchMs: 0,
        };
        this.ballHistory.set(track.id, hist);
      }

      const goingUp = track.vy > VY_UP_THRESHOLD;
      const goingDown = track.vy < VY_DOWN_THRESHOLD;
      const speed = Math.hypot(track.vx, track.vy);

      if (hist.wasGoingUp && goingDown && timestampMs - hist.lastThrowMs > EVENT_COOLDOWN_MS) {
        const hand = this.attributeHand(track.x, track.y, hands);
        const event: ThrowEvent = {
          ballId: track.id,
          hand,
          apexY: track.y,
          timestampMs,
        };
        newThrows.push(event);
        this.totalThrows++;
        if (hand === 'left') this.leftThrows++;
        else this.rightThrows++;
        this.peakHeights.push(track.y);
        hist.lastThrowMs = timestampMs;
      }

      if (
        speed < CATCH_SPEED_THRESHOLD &&
        hist.prevVy < VY_DOWN_THRESHOLD &&
        timestampMs - hist.lastCatchMs > EVENT_COOLDOWN_MS
      ) {
        const hand = this.attributeHand(track.x, track.y, hands);
        const event: CatchEvent = {
          ballId: track.id,
          hand,
          timestampMs,
        };
        newCatches.push(event);
        this.totalCatches++;
        if (hand === 'left') this.leftCatches++;
        else this.rightCatches++;
        hist.lastCatchMs = timestampMs;
      }

      hist.prevVy = track.vy;
      hist.wasGoingUp = goingUp;
    }

    for (const e of [...newThrows, ...newCatches]) {
      this.lastEvents.push(e);
      if (this.lastEvents.length > 20) this.lastEvents.shift();
    }

    return {
      throwCount: newThrows.length,
      catchCount: newCatches.length,
      throws: newThrows,
      catches: newCatches,
    };
  }

  private attributeHand(ballX: number, ballY: number, hands: HandPosition[]): 'left' | 'right' {
    if (hands.length === 0) return 'right';
    let bestHand: 'left' | 'right' = 'right';
    let bestDist = Infinity;
    for (const h of hands) {
      const d = Math.hypot(ballX - h.x, ballY - h.y);
      if (d < bestDist && d < HAND_ATTRIBUTION_RADIUS) {
        bestDist = d;
        bestHand = h.isLeft ? 'left' : 'right';
      }
    }
    return bestHand;
  }

  getState(): EventDetectorState {
    return {
      totalThrows: this.totalThrows,
      totalCatches: this.totalCatches,
      leftThrows: this.leftThrows,
      rightThrows: this.rightThrows,
      leftCatches: this.leftCatches,
      rightCatches: this.rightCatches,
      peakHeights: [...this.peakHeights],
      lastEvents: [...this.lastEvents],
    };
  }

  getLeftRightBalance(): { leftPct: number; rightPct: number } {
    const total = this.leftThrows + this.rightThrows;
    if (total === 0) return { leftPct: 50, rightPct: 50 };
    return {
      leftPct: (this.leftThrows / total) * 100,
      rightPct: (this.rightThrows / total) * 100,
    };
  }

  getMeanPeakHeight(): number {
    if (this.peakHeights.length === 0) return 0;
    const sum = this.peakHeights.reduce((a, b) => a + b, 0);
    return sum / this.peakHeights.length;
  }

  reset(): void {
    this.ballHistory.clear();
    this.totalThrows = 0;
    this.totalCatches = 0;
    this.leftThrows = 0;
    this.rightThrows = 0;
    this.leftCatches = 0;
    this.rightCatches = 0;
    this.peakHeights = [];
    this.lastEvents = [];
  }
}
